import { graphql, rest } from 'services/api';
import * as mutations from './mutations';
import * as queries from './queries';
import { buildAnalysisSearchClauses } from 'utils/buildAnalysisSearchClauses';
import {
  Candidate,
  CandidateBatch,
  CandidateBatchGroup,
  CandidateBatchStatus,
  CandidateEmailCorrespondence,
  CandidateNote,
  CandidateStatus,
  CandidateWithCandidateBatch,
  CorrespondenceStatus,
  ProcessCandidateBatchesInput,
  ProcessCandidateBatchesOutput,
  Requirement,
  Rubric,
  SearchableCandidateBatchSortInput,
  SearchableCandidateSortInput,
  UpdateAnalysisRequirementsInput
} from './types';
import {
  ModelSortDirection,
  SearchableStringFilterInput
} from 'services/api/types';
import { Weights } from 'pages/CandidatesRanking/types';
import { AutomationTaskInput as DeprecatedAutomationTaskInput } from 'services/automation/types';
import { sortOnScheduleDate } from './utils/sortOnScheduleDate';
import { AutomationTaskInput } from 'generated-graphql/graphql';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';

export const createCandidateBatch = async ({
  cvS3Keys,
  csvS3Key,
  jobDescription,
  status,
  rubric,
  organizationId,
  weights,
  userId
}: {
  cvS3Keys?: Array<string> | null;
  csvS3Key?: string | null;
  jobDescription: string;
  status: CandidateBatchStatus;
  rubric: Rubric;
  weights: Weights;
  organizationId: string;
  userId?: string;
}): Promise<CandidateBatch> => {
  const response = await graphql.mutation(mutations.createCandidateBatch, {
    cvS3Keys,
    csvS3Key,
    jobDescription,
    status,
    rubric,
    weights,
    organizationId,
    userId
  });

  return response.createCandidateBatch;
};

export const getCandidatesByCandidatesBatch = async ({
  candidateBatchId,
  filter
}: {
  candidateBatchId: string;
  filter: any;
}): Promise<{ items: Candidate[] }> => {
  const items = [];
  const params = {
    candidateBatchId,
    sortDirection: ModelSortDirection.DESC,
    // TODO: Add pagination support on the candidates table to not fetch everything at once
    // The limit is set to 1000 more than that will ignore the rest of the candidates
    limit: 1000,
    filter
  };
  let nextToken = null;

  const response = await graphql.query(
    queries.getCandidatesByCandidatesBatchAndScoreScaled,
    params
  );

  items.push(...response.getCandidatesByCandidatesBatchAndScoreScaled.items);
  nextToken = response.getCandidatesByCandidatesBatchAndScoreScaled.nextToken;

  while (nextToken) {
    const nextResponse = await graphql.query(
      queries.getCandidatesByCandidatesBatchAndScoreScaled,
      {
        ...params,
        nextToken
      }
    );

    items.push(
      ...nextResponse.getCandidatesByCandidatesBatchAndScoreScaled.items
    );

    nextToken =
      nextResponse.getCandidatesByCandidatesBatchAndScoreScaled.nextToken;
  }

  return {
    items
  };
};

export const getCandidate = async ({
  id
}: {
  id: string;
}): Promise<Candidate> => {
  const response = await graphql.query(queries.getCandidate, { id });

  return response.getCandidate;
};

export const getCandidateWithCandidateBatch = async ({
  id
}: {
  id: string;
}): Promise<CandidateWithCandidateBatch> => {
  const response = await graphql.query(queries.getCandidateWithCandidateBatch, {
    id
  });

  return response.getCandidate;
};

export const getCandidateBatchByOrganization = async ({
  organizationId
}: {
  organizationId: string;
}): Promise<{ items: CandidateBatch[]; nextToken: string }> => {
  let nextToken = null;
  const items = [];

  const response = await graphql.query(
    queries.getCandidateBatchByOrganization,
    {
      organizationId,
      sortDirection: ModelSortDirection.DESC,
      limit: 1000
    }
  );

  items.push(...response.getCandidateBatchByOrganization.items);

  nextToken = response.getCandidateBatchByOrganization.nextToken;

  while (nextToken) {
    const nextResponse = await graphql.query(
      queries.getCandidateBatchByOrganization,
      {
        organizationId,
        sortDirection: ModelSortDirection.DESC,
        limit: 1000,
        nextToken
      }
    );

    items.push(...nextResponse.getCandidateBatchByOrganization.items);
    nextToken = nextResponse.getCandidateBatchByOrganization.nextToken;
  }

  return { items, nextToken };
};

export const getCandidateBatchGroupByOrganization = async ({
  organizationId
}: {
  organizationId: string;
}): Promise<{ items: CandidateBatchGroup[]; nextToken: string }> => {
  const response = await graphql.query(
    queries.getCandidateBatchGroupByOrganization,
    {
      organizationId,
      sortDirection: ModelSortDirection.DESC,
      limit: 500
    }
  );

  return response.getCandidateBatchGroupByOrganization;
};

export const updateCandidate = async ({
  id,
  firstName,
  lastName,
  phoneNumber,
  email,
  status,
  atsRejectedReasonId,
  atsInterviewStageId,
  komboRejectionReasonId,
  komboStageId,
  isLocked
}: {
  id: string;
  phoneNumber?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  status?: CandidateStatus;
  atsRejectedReasonId?: string | null;
  atsInterviewStageId?: string | null;
  komboRejectionReasonId?: string | null;
  komboStageId?: string | null;
  isLocked?: boolean;
}): Promise<Candidate> => {
  const response = await graphql.mutation(mutations.updateCandidate, {
    id,
    firstName,
    lastName,
    phoneNumber,
    email,
    status,
    atsRejectedReasonId,
    atsInterviewStageId,
    komboRejectionReasonId,
    komboStageId,
    isLocked
  });

  return response.updateCandidate;
};

export const startCandidateBatchProcessing = async ({
  candidateBatchId
}: {
  candidateBatchId: string;
}): Promise<CandidateBatch> => {
  const response = await graphql.mutation(
    mutations.startCandidateBatchProcessing,
    {
      candidateBatchId
    }
  );

  return response.startCandidateBatchProcessing;
};

export const updateCandidateBatch = async ({
  id,
  csvS3Key,
  cvS3Keys,
  weights
}: {
  id: string;
  csvS3Key?: string;
  cvS3Keys?: Array<string>;
  weights?: Weights;
}): Promise<CandidateBatch> => {
  const response = await graphql.mutation(mutations.updateCandidateBatch, {
    id,
    csvS3Key,
    cvS3Keys,
    weights
  });

  return response.updateCandidateBatch;
};

export const initializeCandidateBatch = async ({
  candidateBatchId,
  cvS3Keys,
  csvS3Key,
  jobDescription,
  analysisRequirements,
  atsJobId,
  automationTasks,
  atsJobReference
}: {
  candidateBatchId?: string;
  cvS3Keys?: string[];
  csvS3Key?: string;
  jobDescription: string;
  analysisRequirements: AnalysisRequirementInput[];
  atsJobId?: string;
  automationTasks?: DeprecatedAutomationTaskInput[];
  atsJobReference?: string;
}): Promise<CandidateBatch> => {
  const response = await graphql.mutation(mutations.initializeCandidateBatch, {
    candidateBatchId,
    cvS3Keys,
    csvS3Key,
    jobDescription,
    atsJobId,
    automationTasks,
    requirements: analysisRequirements,
    atsJobReference
  });

  return response.initializeCandidateBatch;
};

export const initializeCandidateBatchGroup = async ({
  name,
  analysisRequirements,
  automationTasks
}: {
  name: string;
  analysisRequirements: AnalysisRequirementInput[];
  automationTasks?: (DeprecatedAutomationTaskInput | AutomationTaskInput)[];
}): Promise<CandidateBatchGroup> => {
  const response = await graphql.mutation(
    mutations.initializeCandidateBatchGroup,
    {
      name,
      automationTasks,
      analysisRequirements
    }
  );

  return response.initializeCandidateBatchGroup;
};

export const getProfileData = async ({ profileId }: { profileId: string }) => {
  const response = await graphql.query(queries.getProfileData, { profileId });
  return response.getProfileData;
};

export const generateJobRequirements = async ({
  jobDescription
}: {
  jobDescription: string;
}): Promise<{
  requirements: Requirement[];
}> => {
  const response = await graphql.query(queries.generateJobRequirements, {
    jobDescription
  });

  return response.generateJobRequirements;
};

export const stopAnalysis = async ({
  candidateBatchId
}: {
  candidateBatchId: string;
}): Promise<CandidateBatch> => {
  const response = await graphql.mutation(mutations.updateCandidateBatch, {
    id: candidateBatchId,
    status: CandidateBatchStatus.ARCHIVED
  });

  return response.updateCandidateBatchStatus;
};

export const exportAnalysisData = async ({
  candidateBatchId
}: {
  candidateBatchId: string;
}): Promise<{
  url: string;
}> => {
  const response = await graphql.query(queries.exportAnalysisData, {
    candidateBatchId
  });

  return response.exportAnalysisData;
};

export const createCandidateNote = async ({
  content,
  userId,
  organizationId,
  candidateId
}: {
  candidateId: string;
  content: string;
  userId: string;
  organizationId: string;
}): Promise<CandidateNote> => {
  const response = await graphql.mutation(mutations.createCandidateNote, {
    candidateId,
    content,
    userId,
    organizationId
  });

  return response.createCandidateNote;
};

export const getCandidateNotesByCandidate = async ({
  candidateId
}: {
  candidateId: string;
}) => {
  const response = await graphql.query(queries.getCandidateNotesByCandidate, {
    candidateId,
    limit: 50
  });

  return response.getCandidateNotesByCandidate.items;
};

export const updateCandidateNote = async ({
  id,
  content
}: {
  id: string;
  content: string;
}): Promise<CandidateNote> => {
  const response = await graphql.mutation(mutations.updateCandidateNote, {
    id,
    content
  });

  return response.updateCandidateNote;
};

export const deleteCandidateNote = async ({
  id
}: {
  id: string;
}): Promise<CandidateNote> => {
  const response = await graphql.mutation(mutations.deleteCandidateNote, {
    id
  });

  return response.deleteCandidateNote;
};

export const getAnalysisRequirementsByCandidateBatch = async ({
  candidateBatchId
}: {
  candidateBatchId: string;
}) => {
  const response = await graphql.query(
    queries.getAnalysisRequirementsByCandidateBatch,
    { candidateBatchId, limit: 100 }
  );

  return response.getAnalysisRequirementsByCandidateBatch.items;
};

export const updateAnalysisRequirements = async (
  input: UpdateAnalysisRequirementsInput
) => {
  const response = await graphql.mutation(
    mutations.updateAnalysisRequirements,
    input
  );

  return response.updateAnalysisRequirements;
};

export const getCandidateEmailCorrespondencesByCandidate = async ({
  candidateId
}: {
  candidateId: string;
}): Promise<CandidateEmailCorrespondence[]> => {
  const response = await graphql.query(
    queries.getCandidateEmailCorrespondencesByCandidate,
    { candidateId }
  );

  const correspondences: CandidateEmailCorrespondence[] =
    response.getCandidateEmailCorrespondencesByCandidate.items;

  return sortOnScheduleDate(correspondences);
};

export const updateCandidateEmailCorrespondence = async ({
  id,
  status
}: {
  id: string;
  status: CorrespondenceStatus;
}): Promise<CandidateEmailCorrespondence> => {
  const response = await graphql.mutation(
    mutations.updateCandidateEmailCorrespondence,
    {
      id,
      status
    }
  );

  return response.updateCandidateEmailCorrespondence;
};

export const senEmailCorrespondence = async ({
  candidateEmailCorrespondenceId
}: {
  candidateEmailCorrespondenceId: string;
}) => {
  const response = await rest.post('/notification/send-email-correspondence', {
    candidateEmailCorrespondenceId
  });

  return response;
};

export const getCandidateBatchesByCandidateBatchGroup = async ({
  candidateBatchGroupId
}: {
  candidateBatchGroupId: string;
}) => {
  const items = [];
  let nextToken = null;

  const response = await graphql.query(
    queries.getCandidateBatchesByCandidateBatchGroup,
    {
      candidateBatchGroupId,
      limit: 1000
    }
  );

  items.push(...response.getCandidateBatchesByCandidateBatchGroup.items);

  nextToken = response.getCandidateBatchesByCandidateBatchGroup.nextToken;

  while (nextToken) {
    const nextResponse = await graphql.query(
      queries.getCandidateBatchesByCandidateBatchGroup,
      {
        candidateBatchGroupId,
        limit: 1000,
        nextToken
      }
    );

    items.push(...nextResponse.getCandidateBatchesByCandidateBatchGroup.items);

    nextToken = nextResponse.getCandidateBatchesByCandidateBatchGroup.nextToken;
  }

  return items;
};

export const getCandidateBatchGroup = async ({
  id
}: {
  id: string;
}): Promise<CandidateBatchGroup> => {
  const response = await graphql.query(queries.getCandidateBatchGroup, { id });

  return response.getCandidateBatchGroup;
};

export const getAnalysisRequirementsByCandidateBatchGroup = async ({
  candidateBatchGroupId
}: {
  candidateBatchGroupId: string;
}) => {
  const response = await graphql.query(
    queries.getAnalysisRequirementsByCandidateBatchGroup,
    {
      candidateBatchGroupId
    }
  );

  return response.getAnalysisRequirementsByCandidateBatchGroup.items;
};

export const updateCandidateBatchGroup = async ({
  id,
  name
}: {
  id: string;
  name: string;
}) => {
  const response = await graphql.mutation(mutations.updateCandidateBatchGroup, {
    id,
    name
  });

  return response.updateCandidateBatchGroup;
};

export const processCandidateBatches = async (
  input: ProcessCandidateBatchesInput
): Promise<ProcessCandidateBatchesOutput> => {
  const response = await graphql.mutation(
    mutations.processCandidateBatches,
    input
  );

  return response.processCandidateBatches;
};

export const addCVsToCandidateBatch = async ({
  candidateBatchId,
  cvS3Keys
}: {
  candidateBatchId: string;
  cvS3Keys: string[];
}) => {
  const response = await graphql.mutation(mutations.addCVsToCandidateBatch, {
    candidateBatchId,
    cvS3Keys
  });

  return response.addCVsToCandidateBatch;
};

export const searchCandidates = async ({
  nextToken,
  limit = 10,
  variables
}: {
  variables: {
    organizationId: string;
    search?: string;
    sort?: SearchableCandidateSortInput;
    filters?: {
      filterId: string;
      value: string;
    }[];
  };
  nextToken: string | null;
  limit?: number;
}) => {
  const { organizationId, search, sort, filters } = variables;

  const getFilter = () => {
    const filter: any = {
      organizationId: {
        eq: organizationId
      }
    };

    if (search) {
      filter['or'] = [
        {
          firstName: {
            match: search
          }
        },
        {
          lastName: {
            match: search
          }
        },
        {
          firstName: {
            matchPhrasePrefix: search
          }
        },
        {
          lastName: {
            matchPhrasePrefix: search
          }
        }
      ];
    }

    if (filters?.length) {
      filter['and'] = filters.map((filter) => ({
        [filter.filterId]: {
          eq: filter.value
        }
      }));
    }

    return filter;
  };

  const response = await graphql.query(queries.searchCandidates, {
    limit,
    nextToken,
    sort: sort || { direction: 'desc', field: 'createdAt' },
    filter: getFilter()
  });

  return response.searchCandidates;
};

export const searchCandidateBatches = async ({
  nextToken,
  limit = 10,
  variables
}: {
  variables: {
    organizationId: string;
    search?: string;
    userId?: string;
    candidateBatchGroupId?: string;
    sort?: SearchableCandidateBatchSortInput;
  };
  nextToken: string | null;
  limit?: number;
}) => {
  const { organizationId, search, sort, userId, candidateBatchGroupId } =
    variables;

  const getFilter = () => {
    const filter: {
      or?: any;
      organizationId: SearchableStringFilterInput;
      userId?: SearchableStringFilterInput;
      candidateBatchGroupId?: SearchableStringFilterInput;
    } = {
      organizationId: {
        eq: organizationId
      }
    };

    if (search) {
      filter['or'] = buildAnalysisSearchClauses(search);
    }

    if (userId) {
      filter['userId'] = {
        eq: userId
      };
    }

    if (organizationId) {
      filter['organizationId'] = {
        eq: organizationId
      };
    }

    if (candidateBatchGroupId) {
      filter['candidateBatchGroupId'] = {
        eq: candidateBatchGroupId
      };
    }

    return filter;
  };

  const response = await graphql.query(queries.searchCandidateBatches, {
    limit,
    nextToken,
    sort: sort || { direction: 'desc', field: 'updatedAt' },
    filter: getFilter()
  });

  return response.searchCandidateBatches;
};

export const exportAllAnalysisData = async () => {
  const response = await graphql.query(queries.exportAllAnalysisData);

  return response.exportAllAnalysisData;
};
